import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '../../hooks/useInView';

// Splits "1.4M+", "98%", "4 hrs", "R 4.17M" into prefix / number / suffix.
const parse = (value) => {
  if (/\d\s*\/\s*\d/.test(String(value))) return null; // "24/7" reads as a phrase, not a quantity
  const match = /^([^\d]*)(\d[\d,]*\.?\d*)(.*)$/.exec(String(value));
  if (!match) return null;
  const raw = match[2].replace(/,/g, '');
  const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
  return { prefix: match[1], number: parseFloat(raw), decimals, suffix: match[3] };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts a display value up from zero once `start` is true.
 * Screen readers always get the final value.
 */
const CountUp = ({ value, start = true, duration = 1400, className }) => {
  const parts = parse(value);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!parts || !start) return undefined;
    if (prefersReducedMotion()) {
      setCurrent(parts.number);
      return undefined;
    }
    let frame;
    const begin = performance.now();
    const tick = (now) => {
      // A frame's timestamp can predate `begin` slightly, so clamp at 0 as well
      const t = Math.min(Math.max((now - begin) / duration, 0), 1);
      setCurrent(parts.number * easeOutCubic(t));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    // Background tabs pause animation frames; make sure the final value always lands
    const settle = setTimeout(() => setCurrent(parts.number), duration + 300);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, start, duration]);

  if (!parts) return <span className={className}>{value}</span>;

  // Point decimals, space thousands ("12 480"), as in the rest of the site
  const shown = current
    .toLocaleString('en-US', {
      minimumFractionDigits: parts.decimals,
      maximumFractionDigits: parts.decimals,
    })
    .replace(/,/g, ' ');

  return (
    <span className={className} aria-label={String(value)}>
      <span aria-hidden="true">
        {parts.prefix}
        {shown}
        {parts.suffix}
      </span>
    </span>
  );
};

export default CountUp;
